import { Component, EventEmitter, Input, Output, OnInit, OnChanges, OnDestroy, SimpleChanges, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { StorageService, UtilsService } from 'src/app/services';
import { images } from '../../../../images';
import { CommunicationType, CommunicationUserType, SuggestionSortType, UserRole } from 'src/app/models/enums';
import {
  DEFAULT_SUGGESTION_SORT,
  deserializeMessagesFilters,
  deserializeSuggestionsFilters,
  parseMessagesFiltersFromStorage,
  parseSuggestionsFiltersFromStorage,
} from 'src/app/filters';

@Component({
  selector: 'app-communication-search-bar',
  templateUrl: './communication-search-bar.component.html',
  styleUrl: './communication-search-bar.component.scss',
  standalone: false
})
export class CommunicationSearchBarComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private queryParamsSub?: Subscription;

  @Input() searchValue = '';
  @Input() placeholder = '';
  @Input() searching = false;
  @Input() communicationType = CommunicationType.SUGGESTIONS;
  @Input() communicationUserType = CommunicationUserType.USERS;
  @Output() addCommunicationEventEmitter = new EventEmitter();
  @Output() filter = new EventEmitter<any>();
  @Output() searchEventEmitter = new EventEmitter<string>();
  @Output() sort = new EventEmitter();
  @Output() changeCommunicationUserTypeEmitter = new EventEmitter();
  @Output() changeCommunicationTypeEmitter = new EventEmitter();

  public filterOpened = false;
  public filtersApplied = 0;

  public sortedOpened = false;
  public suggestionSortTypeSelected: SuggestionSortType = DEFAULT_SUGGESTION_SORT;
  public sortOptions: any[] = [];

  public communicationTypeOpened = false;
  public communicationTypesOptions: any[] = [];

  public images = images;

  public CommunicationTypes = CommunicationType;
  public CommunicationUserTypes = CommunicationUserType;
  public adminRole: UserRole;

  constructor(
    public literalService: LiteralService,
    public storageService: StorageService,
    public utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
  ) { }

  ngOnInit() {
    this.getRole();
    this.updateFilters();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() => {
      this.applySortFromUrl();
      this.updateFilters();
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['communicationType'] || changes['communicationUserType']) this.updateFilters();
  }

  ngAfterViewInit(): void {
    this.initSelectorOptions();
  }

  private applySortFromUrl() {
    const sortFromUrl = this.activatedRoute.snapshot.queryParamMap.get('sort') as SuggestionSortType | null;
    this.suggestionSortTypeSelected = sortFromUrl ?? DEFAULT_SUGGESTION_SORT;
  }

  async getRole() {
    this.adminRole = await this.storageService.getRole();
  }

  async initSelectorOptions() {
    Object.values(CommunicationType).forEach((communicationType: CommunicationType) => {
      this.communicationTypesOptions.push({
        value: communicationType,
        icon: images.suggestions[`${communicationType}Icon`],
        label: `communications.types.${communicationType}`
      });
    });
    await this.applySortFromUrl();
    Object.values(SuggestionSortType).forEach((sortType: SuggestionSortType) => {
      this.sortOptions.push({
        value: sortType,
        icon: images.suggestions.sortOptions[sortType.toLowerCase() as keyof typeof images.suggestions.sortOptions],
        label: `suggestions.actions.sort.${sortType}`
      });
    });
  }

  search(event: any) {
    this.searchEventEmitter.emit(event);
  }

  changeCommunicationUserType() {
    this.changeCommunicationUserTypeEmitter.emit();
  }

  changeCommunicationType(value: CommunicationType) {
    this.changeCommunicationTypeEmitter.emit(value);
  }

  async handleSort(newValue: SuggestionSortType) {
    this.suggestionSortTypeSelected = newValue;
    await this.storageService.setSuggestionsSortType(newValue);
    this.sort.emit();
  }

  closeFilter() {
    this.filterOpened = false;
  }

  async updateFilters() {
    const params = this.activatedRoute.snapshot.queryParamMap;
    let filtersForCount: any | null = null;
    if (this.communicationType === CommunicationType.SUGGESTIONS) {
      filtersForCount = params.keys.length > 0
        ? deserializeSuggestionsFilters(params)
        : parseSuggestionsFiltersFromStorage(await this.storageService.getSuggestionsFilters());
    } else if (this.communicationType === CommunicationType.MESSAGES) {
      filtersForCount = params.keys.length > 0
        ? deserializeMessagesFilters(params)
        : parseMessagesFiltersFromStorage(await this.storageService.getMessagesFilters());
    }
    this.filtersApplied = filtersForCount ? this.utilsService.getFiltersCount(filtersForCount) : 0;
  }
}
